import React from 'react';
import { Trash2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { es } from 'date-fns/locale';
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { useIsDarkTheme } from '@/hooks/useIsDarkTheme';
import { useTranslation } from '@/hooks/useTranslation';
import { useTheme } from '@/contexts/ThemeProvider';
import { getSurfaceContrastColors } from '@/lib/contrastUtils';

interface MessagePostItProps {
  id?: string;
  message: string;
  authorName: string;
  authorAvatar?: string | null;
  dateCreated: string;
  onEdit?: () => void;
  onDelete?: () => void;
  isOwnMessage?: boolean;
  hideDate?: boolean;
  animate?: boolean;
  authorFontSize?: string;
  rotation?: number;
  /**
   * Enables the list slide-up animation used on the /mensajes page.
   * Should be disabled when the parent controls opacity/visibility (e.g., GaleriaEnVivo).
   */
  enableListAnimation?: boolean;
}

const MessagePostIt = ({
  id,
  message,
  authorName,
  authorAvatar = "https://cdn.imgbin.com/24/14/3/3d-woman-avatar-stylized-cartoon-woman-avatar-with-glasses-knJAM2pV.jpg",
  dateCreated,
  isOwnMessage,
  onEdit,
  onDelete,
  hideDate = false,
  animate = false,
  authorFontSize,
  rotation = 0,
  enableListAnimation = true,
}: MessagePostItProps) => {
  const { t, currentLanguage } = useTranslation();
  const { tokens } = useTheme();
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      // Use currentLanguage from translation hook to determine locale
      const dateLocale = currentLanguage?.startsWith('es') ? es : enUS;
      return format(date, "d 'de' MMM 'a las' HH:mm", { locale: dateLocale });
    } catch {
      return '';
    }
  };

  const isDark = useIsDarkTheme();
  const tapeSrc = isDark ? '/img/washi-tape-dark.png' : '/img/washi-tape.png';
  const noteSurface = tokens.colors.primary || tokens.colors.background || '0 0% 100%';
  const preferredNoteText = tokens.colors['primary-foreground'] || tokens.colors.foreground;
  const noteColors = React.useMemo(
    () => getSurfaceContrastColors(noteSurface, preferredNoteText),
    [noteSurface, preferredNoteText],
  );

  // Typing animation config (gallery only when animate=true)
  const CHAR_DELAY_MS = 35;
  const chars = React.useMemo(() => (animate ? Array.from(message) : []), [animate, message]);
  const typingDurationMs = animate ? chars.length * CHAR_DELAY_MS : 0;
  const authorAnimationStyle = animate
    ? ({ '--author-delay': `${typingDurationMs}ms` } as React.CSSProperties)
    : undefined;

  // Add CSS for slide-up animation with nth-child delays and rotations.
  // Use layout effect so styles are inserted before first paint to avoid flash.
  const injectedRef = React.useRef(false);
  React.useLayoutEffect(() => {
    if (!enableListAnimation || injectedRef.current) return;
    const existing = document.querySelector('style[data-bk-postit]');
    if (existing) {
      injectedRef.current = true;
      return;
    }
    const style = document.createElement('style');
    style.setAttribute('data-bk-postit', '1');
    style.textContent = `
      @keyframes slideUpIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
      .message-post-it { opacity: 0; animation: slideUpIn 0.5s ease-out forwards; }
      .message-list > .message-post-it:nth-child(5n+1) { animation-delay: 0s; }
      .message-list > .message-post-it:nth-child(5n+2) { animation-delay: 0.1s; }
      .message-list > .message-post-it:nth-child(5n+3) { animation-delay: 0.2s; }
      .message-list > .message-post-it:nth-child(5n+4) { animation-delay: 0.3s; }
      .message-list > .message-post-it:nth-child(5n+5) { animation-delay: 0.4s; }
      .message-list > .message-post-it:nth-child(5n+1) .message-post-it-card { transform: rotate(-1.2deg); }
      .message-list > .message-post-it:nth-child(5n+2) .message-post-it-card { transform: rotate(0.8deg); }
      .message-list > .message-post-it:nth-child(5n+3) .message-post-it-card { transform: rotate(-0.5deg); }
      .message-list > .message-post-it:nth-child(5n+4) .message-post-it-card { transform: rotate(1.1deg); }
      .message-list > .message-post-it:nth-child(5n+5) .message-post-it-card { transform: rotate(-0.9deg); }
    `;
    document.head.appendChild(style);
    injectedRef.current = true;
    // Intentionally do not remove on unmount; shared global style for the page.
  }, [enableListAnimation]);

  return (
    <div className={`${enableListAnimation ? 'message-post-it' : ''} relative px-6 py-4 mb-8 min-w-80 max-w-96 md:min-w-80 md:max-w-none mx-auto`}>
      {/* Washi tape decoration - positioned at top center, overlapping the note */}
      <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10" style={{ maxWidth: '130px' }}>
        <img
          src={tapeSrc}
          alt="washi tape"
          className="w-full h-9"
        />
      </div>

      {/* Post-it card */}
      <div
        className="message-post-it-card relative bg-primary text-primary-foreground border-primary/20 rounded-lg p-9 pt-12 pb-5 hover:shadow-lg transition-shadow mt-2 shadow-lg flex flex-col justify-between"
        style={{
          transform: `rotate(${rotation}deg)`,
          ['--message-note-fg' as string]: noteColors.foreground,
          ['--message-note-muted-fg' as string]: noteColors.mutedForeground,
        }}
      >
        {/* Edit/Delete buttons - positioned in top corners */}
        {(onEdit || onDelete) && (
          <div className="absolute top-2 left-2 right-2 flex justify-between z-20 pointer-events-none">
            {/* Left side - Delete button */}
            {onDelete && (
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-full pointer-events-auto text-[hsl(var(--message-note-muted-fg))] hover:bg-[hsl(var(--message-note-fg)/0.12)] hover:text-[hsl(var(--message-note-fg))] focus-visible:bg-[hsl(var(--message-note-fg)/0.12)] focus-visible:text-[hsl(var(--message-note-fg))]"
                onClick={onDelete}
                title={t('deleteTooltip')}
                aria-label={t('deleteTooltip')}
              >
                <Trash2 className="w-5 h-5" />
              </Button>
            )}
            {/* Right side - Edit button */}
            {onEdit && (
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-full pointer-events-auto text-[hsl(var(--message-note-muted-fg))] hover:bg-[hsl(var(--message-note-fg)/0.12)] hover:text-[hsl(var(--message-note-fg))] focus-visible:bg-[hsl(var(--message-note-fg)/0.12)] focus-visible:text-[hsl(var(--message-note-fg))]"
                onClick={onEdit}
                title={t('editTooltip')}
                aria-label={t('editTooltip')}
              >
                <Pencil className="w-5 h-5" />
              </Button>
            )}
          </div>
        )}

        {/* Message content */}
        <div className="text-2xl text-[hsl(var(--message-note-fg))] leading-relaxed whitespace-pre-wrap break-words mb-4 message-content">
          {animate ? (
            chars.map((ch, i) => (
              <span
                key={i}
                className="typing-char"
                style={{ animationDelay: `${i * CHAR_DELAY_MS}ms` }}
              >
                {ch === ' ' ? '\u00A0' : ch}
              </span>
            ))
          ) : (
            message
          )}
        </div>

        {/* Author name and date - positioned at bottom right */}
        <div
          className={`text-right mt-auto message-author ${animate ? 'animate-author-in' : ''}`}
          style={authorAnimationStyle}
        >
          <p
            className="font-semibold text-[hsl(var(--message-note-fg))]"
            style={{
              fontFamily: '"MonteCarlo", cursive',
              fontSize: authorFontSize || '1.7rem',
            }}
          >
            {authorName}
          </p>
          {!hideDate && (
            <p className="text-sm text-[hsl(var(--message-note-muted-fg))]">
              {formatDate(dateCreated)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessagePostIt;
